'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { CheckCircle2, ChevronRight, TrendingDown } from 'lucide-react';
import type { FunnelStage, FunnelStageCount } from '@/features/negociacoes/types';
import { FUNNEL_STAGE_LABELS } from '@/features/negociacoes/types';
import type { PainelCasosSource } from '../types/analytics.types';
import { computeFunnelPercentages } from '../lib/funnelMath';
const BAR_MAX = 138;
const NAME_HEIGHT = 30;
interface StageMeta {
  label: string;
  desc: string;
  color: string;
  pctColor: string;
  darkTextOnBar: boolean;
}
const STAGE_META: Record<FunnelStage, StageMeta> = {
  DISPARADAS: {
    label: FUNNEL_STAGE_LABELS.DISPARADAS,
    desc: 'Pelo menos uma mensagem enviada no período — cada caso conta uma única vez',
    color: 'var(--color-chart-accent-light)',
    pctColor: 'var(--color-chart-accent)',
    darkTextOnBar: true
  },
  RECEBIDAS: {
    label: FUNNEL_STAGE_LABELS.RECEBIDAS,
    desc: 'Mensagem confirmada como entregue no WhatsApp — receber não implica leitura',
    color: 'var(--color-chart-accent)',
    pctColor: 'var(--color-chart-secondary)',
    darkTextOnBar: true
  },
  LIDAS: {
    label: FUNNEL_STAGE_LABELS.LIDAS,
    desc: 'O contato abriu a conversa — ler não implica que respondeu',
    color: 'var(--color-chart-secondary)',
    pctColor: 'var(--color-chart-secondary)',
    darkTextOnBar: false
  },
  RESPONDERAM: {
    label: FUNNEL_STAGE_LABELS.RESPONDERAM,
    desc: 'O contato enviou pelo menos uma mensagem de volta ao agente de IA',
    color: 'var(--color-chart-primary)',
    pctColor: 'var(--color-chart-primary)',
    darkTextOnBar: false
  },
  ACORDO_FECHADO: {
    label: FUNNEL_STAGE_LABELS.ACORDO_FECHADO,
    desc: 'Registro de assinatura do termo de acordo — por IA ou com intervenção humana',
    color: 'var(--color-success-500)',
    pctColor: 'var(--color-success-600)',
    darkTextOnBar: false
  }
};
interface FunilConversaoCardProps {
  data: FunnelStageCount[];
  onStageClick: (source: PainelCasosSource) => void;
}
interface StageView extends FunnelStageCount, StageMeta {
  num: number;
  pctFirst: number;
  pctPrev: number | null;
}
const buildStageViews = (data: FunnelStageCount[]): StageView[] => {
  const percentages = computeFunnelPercentages(data);
  return data.map((stageCount, index) => ({
    ...stageCount,
    ...STAGE_META[stageCount.stage],
    num: index + 1,
    pctFirst: percentages[index].pctFirst,
    pctPrev: percentages[index].pctPrev
  }));
};
const findBiggestDrop = (stages: StageView[]) => {
  return stages.reduce<{
    from: string;
    to: string;
    drop: number;
  } | null>((acc, stage, index) => {
    if (index === 0 || stage.pctPrev === null) return acc;
    const drop = 100 - stage.pctPrev;
    if (acc && drop <= acc.drop) return acc;
    return {
      from: stages[index - 1].label,
      to: stage.label,
      drop
    };
  }, null);
};
const StageColumn: React.FC<{
  stage: StageView;
  onClick: () => void;
}> = t0 => {
  const $ = _c(44);
  const {
    stage,
    onClick
  } = t0;
  const barHeight = Math.max(6, stage.pctFirst / 100 * BAR_MAX);
  const fitsInside = barHeight >= 32;
  const textOnBar = stage.darkTextOnBar ? "var(--color-chart-primary)" : "white";
  let t1;
  if ($[0] !== stage.total) {
    t1 = stage.total.toLocaleString("pt-BR");
    $[0] = stage.total;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const totalLabel = t1;
  const t2 = stage.desc;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      height: NAME_HEIGHT
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== stage.color || $[4] !== textOnBar) {
    t4 = {
      background: stage.color,
      color: textOnBar
    };
    $[3] = stage.color;
    $[4] = textOnBar;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== stage.num || $[7] !== t4) {
    t5 = <div className="flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-full text-[9px] font-bold" style={t4}>{stage.num}</div>;
    $[6] = stage.num;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== stage.label) {
    t6 = <span className="w-full text-center text-[10px] leading-[1.15] font-bold break-words text-neutral-900">{stage.label}</span>;
    $[9] = stage.label;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className="mb-2 flex w-full flex-col items-center gap-[3px]" style={t3}>{t5}{t6}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      height: BAR_MAX
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = fitsInside ? "flex-start" : "flex-end";
  const t10 = fitsInside ? 5 : 0;
  let t11;
  if ($[15] !== barHeight || $[16] !== stage.color || $[17] !== t10 || $[18] !== t9) {
    t11 = {
      height: barHeight,
      background: stage.color,
      alignItems: t9,
      paddingTop: t10
    };
    $[15] = barHeight;
    $[16] = stage.color;
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== fitsInside || $[21] !== textOnBar || $[22] !== totalLabel) {
    t12 = fitsInside && <span className="text-[11px] font-bold" style={{
      color: textOnBar
    }}>{totalLabel}</span>;
    $[20] = fitsInside;
    $[21] = textOnBar;
    $[22] = totalLabel;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t11 || $[25] !== t12) {
    t13 = <div className="flex w-full items-end justify-center" style={t8}><div className="flex w-full max-w-[56px] justify-center rounded-t-[6px] rounded-b-[2px] transition-opacity hover:opacity-80" style={t11}>{t12}</div></div>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== fitsInside || $[28] !== totalLabel) {
    t14 = !fitsInside && <span className="mt-1.5 text-[11px] font-bold text-neutral-900">{totalLabel}</span>;
    $[27] = fitsInside;
    $[28] = totalLabel;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== stage.pctColor) {
    t15 = {
      color: stage.pctColor
    };
    $[30] = stage.pctColor;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== stage.pctFirst) {
    t16 = stage.pctFirst.toFixed(1);
    $[32] = stage.pctFirst;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t15 || $[35] !== t16) {
    t17 = <span className="mt-1 text-[10.5px] font-semibold" style={t15}>{t16}%</span>;
    $[34] = t15;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== onClick || $[38] !== stage.desc || $[39] !== t13 || $[40] !== t14 || $[41] !== t17 || $[42] !== t7) {
    t18 = <button type="button" title={t2} onClick={onClick} className="flex min-w-0 flex-1 cursor-pointer flex-col items-center">{t7}{t13}{t14}{t17}</button>;
    $[37] = onClick;
    $[38] = stage.desc;
    $[39] = t13;
    $[40] = t14;
    $[41] = t17;
    $[42] = t7;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  return t18;
};
const DropArrow: React.FC<{
  pctPrev: number | null;
}> = t0 => {
  const $ = _c(6);
  const {
    pctPrev
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      height: BAR_MAX,
      marginTop: NAME_HEIGHT
    };
    t2 = <ChevronRight className="mb-1 h-[13px] w-[13px] shrink-0 text-neutral-300" strokeWidth={2.5} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== pctPrev) {
    t3 = pctPrev !== null ? `-${(100 - pctPrev).toFixed(0)}%` : "";
    $[2] = pctPrev;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <div className="flex w-[26px] shrink-0 flex-col items-center justify-end" style={t1}>{t2}<span className="text-[9.5px] font-semibold whitespace-nowrap text-neutral-400">{t3}</span></div>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
export const FunilConversaoCard: React.FC<FunilConversaoCardProps> = t0 => {
  const $ = _c(42);
  const {
    data,
    onStageClick
  } = t0;
  let t1;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== data || $[1] !== onStageClick) {
    const stages = buildStageViews(data);
    const lastStage = stages[stages.length - 1];
    const biggestDrop = findBiggestDrop(stages);
    let t11;
    if ($[12] !== onStageClick) {
      t11 = stage => onStageClick({
        kind: "funnel",
        stage: stage.stage,
        title: stage.label,
        color: stage.color
      });
      $[12] = onStageClick;
      $[13] = t11;
    } else {
      t11 = $[13];
    }
    const openStage = t11;
    t8 = "flex h-full flex-col rounded-[16px] border border-neutral-100 bg-white p-[22px] shadow-sm";
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div className="mb-4"><h3 className="text-body-bold font-body-bold text-neutral-900">Funil de Conversão</h3><p className="text-caption font-caption text-subtext-color">Clique em uma etapa para ver os casos correspondentes</p></div>;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    let t12;
    if ($[15] !== openStage) {
      t12 = (stage_0, index) => <React.Fragment key={stage_0.stage}>{index > 0 && <DropArrow pctPrev={stage_0.pctPrev} />}<StageColumn stage={stage_0} onClick={() => openStage(stage_0)} /></React.Fragment>;
      $[15] = openStage;
      $[16] = t12;
    } else {
      t12 = $[16];
    }
    t10 = <div className="flex flex-1 items-end"><div className="flex w-full items-start pt-2">{stages.map(t12)}</div></div>;
    t6 = "mt-4 flex gap-7 border-t border-neutral-100 pt-3.5";
    let t13;
    let t14;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <div className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[6px] bg-[var(--kpi-icon-primary-bg)]"><CheckCircle2 className="h-3 w-3" style={{
          color: "var(--kpi-icon-primary-fg)"
        }} /></div>;
      t14 = <div className="text-caption font-caption text-subtext-color uppercase">Conversão Total</div>;
      $[17] = t13;
      $[18] = t14;
    } else {
      t13 = $[17];
      t14 = $[18];
    }
    const t15 = lastStage ? `${lastStage.pctFirst.toFixed(1)}% (${lastStage.label})` : "\u2014";
    if ($[19] !== t15) {
      t7 = <div className="flex items-center gap-2">{t13}<div>{t14}<div className="text-body-bold font-body-bold text-neutral-900">{t15}</div></div></div>;
      $[19] = t15;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    t4 = "flex items-center gap-2";
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[6px] bg-[var(--kpi-icon-warning-bg)]"><TrendingDown className="h-3 w-3" style={{
          color: "var(--kpi-icon-warning-fg)"
        }} /></div>;
      t3 = <div className="text-caption font-caption text-subtext-color uppercase">Maior Queda</div>;
      $[21] = t3;
      $[22] = t5;
    } else {
      t3 = $[21];
      t5 = $[22];
    }
    t1 = "text-body-bold font-body-bold text-neutral-900";
    t2 = biggestDrop ? `${biggestDrop.from} → ${biggestDrop.to} (-${biggestDrop.drop.toFixed(1)}%)` : "\u2014";
    $[0] = data;
    $[1] = onStageClick;
    $[2] = t1;
    $[3] = t10;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t1 = $[2];
    t10 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
    t9 = $[11];
  }
  let t11;
  if ($[23] !== t1 || $[24] !== t2) {
    t11 = <div className={t1}>{t2}</div>;
    $[23] = t1;
    $[24] = t2;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t3) {
    t12 = <div>{t3}{t11}</div>;
    $[26] = t11;
    $[27] = t3;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12 || $[30] !== t4 || $[31] !== t5) {
    t13 = <div className={t4}>{t5}{t12}</div>;
    $[29] = t12;
    $[30] = t4;
    $[31] = t5;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t13 || $[34] !== t6 || $[35] !== t7) {
    t14 = <div className={t6}>{t7}{t13}</div>;
    $[33] = t13;
    $[34] = t6;
    $[35] = t7;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t10 || $[38] !== t14 || $[39] !== t8 || $[40] !== t9) {
    t15 = <div className={t8}>{t9}{t10}{t14}</div>;
    $[37] = t10;
    $[38] = t14;
    $[39] = t8;
    $[40] = t9;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  return t15;
};